// Kiểu dữ liệu + danh mục tham chiếu + dữ liệu mẫu cho module Tài liệu (mock — sẽ thay bằng API khi có backend)
import dayjs from 'dayjs';

export type DocStatus = 'draft' | 'pending' | 'rejected' | 'published' | 'revoked';
/** Trạng thái hiển thị — suy ra từ status + ngày hiệu lực + quan hệ ban hành lại */
export type DocState = 'draft' | 'pending' | 'rejected' | 'upcoming' | 'active' | 'expired' | 'replaced' | 'revoked';

export interface DocType {
  id: string;
  code: string;
  name: string;
  /** Ký hiệu dùng sinh mã tài liệu, vd 001/2026/QĐ-KHOTHI */
  prefix: string;
  active: boolean;
  order: number;
  createdAt: string;
}

export interface DocCategory {
  id: string;
  code: string;
  name: string;
  typeId: string;
  parentId: string | null;
  /** Đang sử dụng: được chọn khi tạo tài liệu mới */
  active: boolean;
  /** Hiển thị trên cây thư mục ở màn Quản lý / Cá nhân */
  visible: boolean;
  order: number;
  createdAt: string;
}

export interface NamedItem { id: string; name: string }
export interface Department extends NamedItem { blockId: string }

export interface Employee {
  id: string;
  username: string;
  name: string;
  title: string;
  deptId: string;
  region: string;
  hq: string;
  unit: string;
}

export interface DocFile {
  id: string;
  name: string;
  size: number;
  ext: string;
  uploadedAt: string;
  uploadedBy: string;
  /** File của dữ liệu mẫu — không có nội dung thật trong IndexedDB */
  demo?: boolean;
}

export interface DocComment { id: string; userId: string; userName: string; content: string; at: string }
export interface DocHistory { at: string; by: string; action: string; note?: string }
export type AccessAction = 'view' | 'download' | 'print' | 'copy_link';
export interface AccessLog { userId: string; userName: string; action: AccessAction; at: string; fileName?: string }

export interface Doc {
  id: string;
  code: string;
  number: string;
  title: string;
  summary: string;
  typeId: string;
  categoryId: string;
  blockId: string;
  unitId: string;
  issuerId: string;
  signerId: string;
  /** YYYY-MM-DD */
  effectiveFrom: string;
  effectiveTo: string | null;
  status: DocStatus;
  /** Dự thảo: người được chia sẻ xem và góp ý trước khi ban hành */
  draftMode: boolean;
  shareAll: boolean;
  /** Chia sẻ cho toàn bộ phòng ban của người tạo */
  internalShare: boolean;
  sharedUserIds: string[];
  emailNotify: boolean;
  replacesId: string | null;
  replacedById: string | null;
  files: DocFile[];
  createdBy: string;
  createdByName: string;
  createdAt: string;
  updatedAt: string;
  submittedAt?: string;
  approvedBy?: string;
  approvedAt?: string;
  rejectReason?: string;
  revokeReason?: string;
  revokedAt?: string;
  deletedAt: string | null;
  deletedBy?: string | null;
  /** Lần rà soát gần nhất (giữ nguyên nội dung) */
  reviewedAt?: string;
  reviewedBy?: string;
  /** userId → thời điểm xem lần đầu */
  views: Record<string, string>;
  comments: DocComment[];
  history: DocHistory[];
  access: AccessLog[];
}

// ---------------------------------------------------------------------------
// Danh mục tham chiếu

export const COMPANY = 'Công ty KHOTHI';
export const CODE_SUFFIX = 'KHOTHI';
export const ME_ID = 'me';
export const TRASH_DAYS = 30;
export const MAX_TOTAL_SIZE = 250 * 1024 * 1024;
export const ALLOWED_EXT = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'png', 'jpg', 'jpeg', 'zip', 'rar'];

export const BLOCKS: NamedItem[] = [
  { id: 'b-mgmt', name: 'Nghiệp vụ quản trị' },
  { id: 'b-adm', name: 'Khối Hành chính - Nhân sự' },
  { id: 'b-fin', name: 'Khối Tài chính - Kế toán' },
  { id: 'b-biz', name: 'Khối Kinh doanh' },
  { id: 'b-ops', name: 'Khối Vận hành' },
];

export const DEPARTMENTS: Department[] = [
  { id: 'd-bgd', name: 'Ban Giám đốc', blockId: 'b-mgmt' },
  { id: 'd-adm', name: 'Phòng Hành chính', blockId: 'b-adm' },
  { id: 'd-hr', name: 'Phòng Nhân sự', blockId: 'b-adm' },
  { id: 'd-acc', name: 'Phòng Kế toán', blockId: 'b-fin' },
  { id: 'd-sales', name: 'Phòng Kinh doanh', blockId: 'b-biz' },
  { id: 'd-cs', name: 'P. Chăm sóc KH', blockId: 'b-biz' },
  { id: 'd-it', name: 'Phòng CNTT', blockId: 'b-ops' },
];

export const ISSUERS: NamedItem[] = [
  { id: 'is-hdqt', name: 'Hội đồng quản trị' },
  { id: 'is-tgd', name: 'Tổng Giám đốc' },
  { id: 'is-gd', name: 'Giám đốc' },
  { id: 'is-tp', name: 'Trưởng phòng' },
];

const NAMES = [
  'Nguyễn Văn An', 'Trần Thị Bình', 'Lê Hoàng Cường', 'Phạm Thu Dung', 'Hoàng Minh Đức', 'Vũ Thị Hạnh', 'Đặng Quốc Huy',
  'Bùi Thị Lan', 'Đỗ Văn Long', 'Ngô Thị Mai', 'Dương Văn Nam', 'Lý Thị Oanh', 'Phan Văn Phúc', 'Trịnh Thị Quỳnh',
  'Võ Văn Sơn', 'Mai Thị Thảo', 'Tạ Văn Toàn', 'Hồ Thị Uyên', 'Châu Văn Vinh', 'Kiều Thị Xuân', 'Lâm Văn Yên',
  'Quách Thị Ánh', 'Tôn Văn Bảo', 'Cao Thị Chi', 'Đinh Văn Dũng', 'La Thị Giang', 'Thái Văn Hải', 'Mạc Thị Hương',
];
const TITLES_BY_INDEX = ['Tổng Giám đốc', 'Phó Giám đốc', 'Trưởng phòng', 'Phó phòng', 'Chuyên viên', 'Nhân viên', 'Thực tập sinh'];
const REGIONS = ['Miền Nam', 'Miền Bắc', 'Miền Trung'];

const slug = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/đ/g, 'd').replace(/Đ/g, 'D').toLowerCase();

export const EMPLOYEES: Employee[] = NAMES.map((name, i) => {
  const dept = DEPARTMENTS[i % DEPARTMENTS.length];
  const title = i === 0 ? TITLES_BY_INDEX[0] : i < 3 ? TITLES_BY_INDEX[1] : i < 10 ? TITLES_BY_INDEX[2] : TITLES_BY_INDEX[3 + (i % 4)];
  const parts = slug(name).split(' ');
  return {
    id: `e${String(i + 1).padStart(2, '0')}`,
    username: `${parts[parts.length - 1]}.${parts[0]}@khothi.vn`,
    name,
    title,
    deptId: i === 0 ? 'd-bgd' : dept.id,
    region: REGIONS[i % 3],
    hq: i % 5 === 0 ? 'Chi nhánh Hà Nội' : 'Văn phòng điều hành',
    unit: COMPANY,
  };
});

/** Người đang đăng nhập trong dữ liệu mẫu — thuộc Phòng Hành chính */
export const meEmployee = (name: string): Employee => ({
  id: ME_ID, username: 'ban@khothi.vn', name, title: 'Chuyên viên', deptId: 'd-adm', region: 'Miền Nam', hq: 'Văn phòng điều hành', unit: COMPANY,
});

/** Người ký: cấp trưởng phòng trở lên */
export const SIGNERS = EMPLOYEES.filter(e => /Giám đốc|Trưởng phòng/.test(e.title));

export const deptName = (id: string) => DEPARTMENTS.find(d => d.id === id)?.name || '—';
export const blockName = (id: string) => BLOCKS.find(b => b.id === id)?.name || '—';
export const issuerName = (id: string) => ISSUERS.find(i => i.id === id)?.name || '—';

// ---------------------------------------------------------------------------
// Dữ liệu mẫu

const iso = (d: dayjs.Dayjs) => d.toISOString();

export function seedTypes(now = dayjs()): DocType[] {
  const rows: [string, string][] = [
    ['Quy định', 'QĐ'], ['Quy trình', 'QT'], ['Cấu trúc', 'CT'], ['Thông báo', 'TB'],
    ['Biểu mẫu', 'BM'], ['Tài liệu họp', 'TLH'], ['Chỉ tiêu', 'CTI'], ['Nghị quyết', 'NQ'],
  ];
  return rows.map(([name, prefix], i) => ({
    id: `t${i + 1}`, code: `G${String(i + 1).padStart(4, '0')}`, name, prefix, active: true, order: i, createdAt: iso(now.subtract(400, 'day')),
  }));
}

export function seedCategories(now = dayjs()): DocCategory[] {
  const rows: [string, string, string, string | null, boolean?][] = [
    ['c1', 'Quy định chung', 't1', null], ['c1-1', 'Quy định nhân sự', 't1', 'c1'], ['c1-2', 'Quy định tài chính', 't1', 'c1'],
    ['c2', 'Quy trình nghiệp vụ', 't2', null], ['c2-1', 'Quy trình nhân sự', 't2', 'c2'], ['c2-2', 'Quy trình kế toán', 't2', 'c2'], ['c2-3', 'Quy trình chăm sóc khách hàng', 't2', 'c2'],
    ['c3', 'Sơ đồ tổ chức', 't3', null],
    ['c4', 'Thông báo nội bộ', 't4', null], ['c4-1', 'Thông báo nghỉ lễ', 't4', 'c4'],
    ['c5', 'Biểu mẫu', 't5', null], ['c5-1', 'Biểu mẫu nhân sự', 't5', 'c5'], ['c5-2', 'Biểu mẫu kế toán', 't5', 'c5'],
    ['c6', 'Biên bản họp', 't6', null],
    ['c7', 'Chỉ tiêu kinh doanh', 't7', null],
    ['c8', 'Nghị quyết HĐQT', 't8', null, false],
  ];
  const counters: Record<string, number> = {};
  return rows.map(([id, name, typeId, parentId, visible]) => {
    const key = parentId || 'root';
    counters[key] = (counters[key] || 0) + 1;
    const rootIdx = id.split('-')[0].slice(1);
    const code = parentId ? `DM${rootIdx.padStart(2, '0')}.${counters[key]}` : `DM${rootIdx.padStart(2, '0')}`;
    return { id, code, name, typeId, parentId, active: true, visible: visible !== false, order: counters[key] - 1, createdAt: iso(now.subtract(380, 'day')) };
  });
}

const mulberry32 = (seed: number) => () => {
  seed |= 0; seed = (seed + 0x6d2b79f5) | 0;
  let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
  t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
};

const SUBJECTS = [
  'cấp phát thiết bị', 'nghỉ Tết Nguyên đán', 'thanh toán công tác phí', 'tuyển dụng nhân sự', 'bổ nhiệm cán bộ', 'bảo mật thông tin',
  'chương trình khuyến mãi', 'đánh giá năng lực', 'đồng phục nhân viên', 'văn phòng phẩm', 'chấm công', 'làm thêm giờ', 'tiếp nhận khiếu nại',
];
const FILE_EXTS = ['pdf', 'pdf', 'pdf', 'docx', 'xlsx', 'pptx', 'png', 'jpg', 'zip'];

/** Người duyệt trong dữ liệu mẫu: Tổng Giám đốc + 2 Phó Giám đốc */
export const APPROVERS = EMPLOYEES.slice(0, 3);
const PENDING_AGE: Record<number, number> = { 4: 0.4, 12: 2.5, 20: 5.5, 33: 9 };
/** Tài liệu mẫu sắp hết hiệu lực: chỉ số → số ngày còn lại */
const EXPIRING_SOON: Record<number, number> = { 7: 8, 17: 24, 22: 41, 29: 58, 36: 77 };

export function seedDocs(types: DocType[], categories: DocCategory[], now = dayjs()): Doc[] {
  const rnd = mulberry32(20261008);
  const pick = <T,>(arr: T[]) => arr[Math.floor(rnd() * arr.length)];
  const between = (a: number, b: number) => a + rnd() * (b - a);
  const leafCats = categories.filter(c => !categories.some(x => x.parentId === c.id));
  const docs: Doc[] = [];
  const seqByPrefixYear: Record<string, number> = {};
  const people = [...EMPLOYEES.map(e => ({ id: e.id, name: e.name, deptId: e.deptId })), { id: ME_ID, name: 'Bạn', deptId: 'd-adm' }];
  const later = (d: dayjs.Dayjs, days: number) => d.add(Math.round(days * 24 * 60), 'minute');

  for (let i = 0; i < 46; i++) {
    const cat = leafCats[i % leafCats.length];
    const type = types.find(t => t.id === cat.typeId)!;
    const author = i % 7 === 3 ? people[people.length - 1] : pick(people.slice(0, -1));
    const signer = pick(SIGNERS);
    const approver = pick(APPROVERS);
    const subject = SUBJECTS[i % SUBJECTS.length];

    // Phân bổ trạng thái: phần lớn đã ban hành, còn lại rải đều các trạng thái để thử luồng
    let status: DocStatus = 'published';
    if (i === 1 || i === 9) status = 'draft';
    else if (PENDING_AGE[i] !== undefined) status = 'pending';
    else if (i === 15) status = 'rejected';
    else if (i === 25) status = 'revoked';

    // Mốc thời gian: soạn thảo → gửi duyệt → (từ chối → gửi lại) → duyệt
    let created = now.subtract(Math.floor(between(2, 520)), 'day').hour(8 + Math.floor(rnd() * 9)).minute(Math.floor(rnd() * 60));
    if (status === 'pending') created = later(now, -(PENDING_AGE[i] + between(1, 3)));
    const history: DocHistory[] = [{ at: iso(created), by: author.name, action: 'Tạo tài liệu' }];
    let submitted: dayjs.Dayjs | undefined;
    let approvedAt: dayjs.Dayjs | undefined;
    let rejectReason: string | undefined;
    if (status !== 'draft') {
      submitted = status === 'pending' ? later(now, -PENDING_AGE[i]) : later(created, between(0.1, 3));
      history.push({ at: iso(submitted), by: author.name, action: 'Gửi duyệt' });
      const bounced = status === 'rejected' || ((status === 'published' || status === 'revoked') && rnd() < 0.2);
      if (bounced) {
        const rejectAt = later(submitted, between(0.3, 3));
        rejectReason = pick(['Bổ sung căn cứ pháp lý và phụ lục mẫu biểu.', 'Thống nhất lại phạm vi áp dụng với các phòng ban.', 'Sai thể thức văn bản, cần chỉnh lại số hiệu.']);
        history.push({ at: iso(rejectAt), by: approver.name, action: 'Từ chối', note: rejectReason });
        if (status !== 'rejected') {
          submitted = later(rejectAt, between(0.5, 4));
          history.push({ at: iso(submitted), by: author.name, action: 'Gửi duyệt' });
        }
      }
      if (status === 'published' || status === 'revoked') {
        approvedAt = later(submitted, 0.05 + Math.pow(rnd(), 2.2) * 6);
        if (approvedAt.isAfter(now)) approvedAt = now.subtract(1, 'hour');
        history.push({ at: iso(approvedAt), by: approver.name, action: 'Duyệt & ban hành' });
      }
    }
    const year = (approvedAt || created).year();
    const k = `${type.prefix}/${year}`;
    seqByPrefixYear[k] = (seqByPrefixYear[k] || 0) + 1;

    // Hiệu lực: từ sau ngày duyệt; một số sắp có hiệu lực, một số có thời hạn / sắp hết hạn
    const from = i % 11 === 5 && approvedAt ? now.add(10 + (i % 20), 'day') : later(approvedAt || created, Math.floor(between(0, 9)));
    let to: dayjs.Dayjs | null = i % 9 === 2 ? from.add(90, 'day') : null;
    if (EXPIRING_SOON[i] !== undefined && approvedAt && from.isBefore(now)) to = now.add(EXPIRING_SOON[i], 'day');

    const fileCount = 1 + Math.floor(rnd() * 3);
    const files: DocFile[] = Array.from({ length: fileCount }, (_, f) => {
      const ext = f === 0 ? 'pdf' : pick(FILE_EXTS);
      return {
        id: `seedf-${i}-${f}`,
        name: `${slug(`${type.name}_${subject}`).replace(/\s+/g, '_')}${f ? `_phu_luc_${f}` : ''}.${ext}`,
        size: Math.floor((ext === 'zip' ? 4000 : ext === 'pptx' ? 2500 : 900) * 1024 * (0.3 + rnd())),
        ext,
        uploadedAt: iso(created),
        uploadedBy: author.name,
        demo: true,
      };
    });

    const shareAll = rnd() > 0.55;
    const sharedUserIds = shareAll ? [] : EMPLOYEES.filter(() => rnd() > 0.6).map(e => e.id).concat(i % 3 === 0 || (status === 'draft' && i === 1) ? [ME_ID] : []);
    const internalShare = rnd() > 0.5;
    const recipients = people.filter(p => p.id !== author.id && (shareAll || sharedUserIds.includes(p.id) || (internalShare && p.deptId === author.deptId)));

    // Lượt đọc: phần lớn đọc trong 1–3 ngày, một phần đọc muộn, một phần chưa đọc
    const views: Record<string, string> = {};
    const access: AccessLog[] = [];
    if (approvedAt) {
      recipients.forEach(p => {
        const r = rnd();
        if (r >= 0.84) return;
        const delay = r < 0.38 ? between(0.02, 1) : r < 0.6 ? between(1, 3) : r < 0.72 ? between(3, 7) : between(7, 30);
        const at = later(approvedAt!, delay);
        if (at.isAfter(now)) return;
        views[p.id] = iso(at);
        access.push({ userId: p.id, userName: p.name, action: 'view', at: iso(at) });
        if (rnd() < 0.3) access.push({ userId: p.id, userName: p.name, action: 'download', at: iso(at.add(3, 'minute')), fileName: files[0].name });
      });
      access.sort((a, b) => a.at.localeCompare(b.at));
    }
    if (status === 'revoked') history.push({ at: iso(now.subtract(5, 'day')), by: APPROVERS[0].name, action: 'Thu hồi', note: 'Văn bản không còn phù hợp với cơ cấu tổ chức mới.' });

    const id = `doc-${String(i + 1).padStart(3, '0')}`;
    docs.push({
      id,
      code: `${String(seqByPrefixYear[k]).padStart(3, '0')}/${year}/${type.prefix}-${CODE_SUFFIX}`,
      number: `${100 + i}/${year}`,
      title: `${type.name} về việc ${subject}`,
      summary: `Văn bản hướng dẫn thống nhất cách thực hiện ${subject} trong toàn ${COMPANY}. Áp dụng cho các phòng ban và chi nhánh trực thuộc.`,
      typeId: type.id,
      categoryId: cat.id,
      blockId: DEPARTMENTS.find(d => d.id === author.deptId)?.blockId || 'b-mgmt',
      unitId: i % 4 === 0 ? 'd-bgd' : author.deptId,
      issuerId: i % 4 === 0 ? 'is-tgd' : pick(ISSUERS).id,
      signerId: signer.id,
      effectiveFrom: from.format('YYYY-MM-DD'),
      effectiveTo: to ? to.format('YYYY-MM-DD') : null,
      status,
      draftMode: status === 'draft',
      shareAll,
      internalShare,
      sharedUserIds,
      emailNotify: true,
      replacesId: null,
      replacedById: null,
      files,
      createdBy: author.id,
      createdByName: author.name,
      createdAt: iso(created),
      updatedAt: iso(approvedAt || submitted || created),
      submittedAt: submitted ? iso(submitted) : undefined,
      approvedBy: approvedAt ? approver.name : undefined,
      approvedAt: approvedAt ? iso(approvedAt) : undefined,
      rejectReason: status === 'rejected' ? rejectReason : undefined,
      revokeReason: status === 'revoked' ? 'Văn bản không còn phù hợp với cơ cấu tổ chức mới.' : undefined,
      revokedAt: status === 'revoked' ? iso(now.subtract(5, 'day')) : undefined,
      deletedAt: null,
      views,
      comments: status === 'draft' ? [
        { id: `cm-${i}-1`, userId: EMPLOYEES[3].id, userName: EMPLOYEES[3].name, content: 'Đề nghị bổ sung thời hạn áp dụng cho chi nhánh Hà Nội.', at: iso(later(created, 0.1)) },
      ] : [],
      history,
      access,
    });
  }

  // Chuỗi ban hành lại: doc-003 và doc-027 cùng danh mục → bản duyệt sau thay thế bản duyệt trước
  const [older, newer] = [docs[2], docs[26]].sort((a, b) => (a.approvedAt || '').localeCompare(b.approvedAt || ''));
  if (older.status === 'published' && newer.status === 'published' && older.categoryId === newer.categoryId) {
    newer.replacesId = older.id;
    older.replacedById = newer.id;
    const dayBefore = dayjs(newer.effectiveFrom).subtract(1, 'day').format('YYYY-MM-DD');
    older.effectiveTo = dayBefore < older.effectiveFrom ? older.effectiveFrom : dayBefore;
    older.history.push({ at: newer.approvedAt || newer.createdAt, by: newer.approvedBy || '', action: `Bị thay thế bởi ${newer.code}` });
    newer.history.push({ at: newer.approvedAt || newer.createdAt, by: newer.approvedBy || '', action: `Ban hành lại, thay thế ${older.code}` });
  }

  // Một tài liệu đã thu hồi rồi chuyển vào thùng rác (tài liệu còn hiệu lực không được xóa)
  const trashed = docs[40];
  trashed.status = 'revoked';
  trashed.revokeReason = 'Ban hành nhầm phiên bản.';
  trashed.revokedAt = iso(now.subtract(4, 'day'));
  trashed.history.push({ at: trashed.revokedAt, by: EMPLOYEES[2].name, action: 'Thu hồi', note: trashed.revokeReason });
  trashed.deletedAt = iso(now.subtract(3, 'day'));
  trashed.deletedBy = EMPLOYEES[2].name;
  trashed.history.push({ at: trashed.deletedAt, by: EMPLOYEES[2].name, action: 'Chuyển vào thùng rác' });

  return docs;
}
